import {
  cloneElement,
  createContext,
  Fragment,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useContext,
} from "react";

type FieldControlProps = {
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "false" | "grammar" | "spelling" | "true";
};

const FieldControlContext = createContext<FieldControlProps>({});

type DescribedContentProps = {
  id?: string;
  role?: string;
};

type FieldProps = {
  id: string;
  label: ReactNode;
  labelFor?: string | false;
  required?: boolean;
  help?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  layout?: "stacked" | "confirmation";
};

const appendDescriptionIds = (...values: Array<string | undefined>): string | undefined => {
  const ids = values.flatMap((value) => value?.split(/\s+/).filter(Boolean) ?? []);
  return ids.length > 0 ? [...new Set(ids)].join(" ") : undefined;
};

const hasInvalidState = (value: FieldControlProps["aria-invalid"]): boolean => value !== undefined && value !== false && value !== "false";

const mergeFieldControlProps = (props: FieldControlProps, context: FieldControlProps): FieldControlProps => {
  const describedBy = appendDescriptionIds(props["aria-describedby"], context["aria-describedby"]);
  const invalid = hasInvalidState(context["aria-invalid"]) ? context["aria-invalid"] : props["aria-invalid"];
  return {
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    ...(invalid === undefined ? {} : { "aria-invalid": invalid }),
  };
};

export const useFieldControlProps = <Props extends FieldControlProps>(props: Props): Props & FieldControlProps => {
  const context = useContext(FieldControlContext);
  return { ...props, ...mergeFieldControlProps(props, context) };
};

const accessibleControl = (children: ReactNode, context: FieldControlProps): ReactNode => {
  if (!isValidElement<FieldControlProps>(children) || children.type === Fragment) return children;
  const control = children as ReactElement<FieldControlProps>;
  if (typeof control.type !== "string" || !["input", "select", "textarea"].includes(control.type)) return children;
  return cloneElement(control, mergeFieldControlProps(control.props, context));
};

const describedContentProps = (content: ReactNode): DescribedContentProps => (isValidElement<DescribedContentProps>(content) ? content.props : {});

/** Label + optional help/error wrapper. Pass one control as children, wired to `id`. */
export const Field = ({ id, label, labelFor, required, help, error, children, layout = "stacked" }: FieldProps) => {
  const inheritedControlProps = useContext(FieldControlContext);
  const helpProps = describedContentProps(help);
  const errorProps = describedContentProps(error);
  const helpId = help ? (helpProps.id ?? `${id}-help`) : undefined;
  const errorId = error ? (errorProps.id ?? `${id}-error`) : undefined;
  const describedBy = appendDescriptionIds(helpId, errorId);
  const fieldControlProps = mergeFieldControlProps(inheritedControlProps, {
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    ...(errorId ? { "aria-invalid": true } : {}),
  });
  const control = accessibleControl(children, fieldControlProps);
  const providedControl = <FieldControlContext.Provider value={fieldControlProps}>{control}</FieldControlContext.Provider>;

  return (
    <div className={`field field--${layout}`}>
      {layout === "confirmation" ? (
        <label className="confirmation-control" htmlFor={id}>
          {providedControl}
          <span className="confirmation-control__copy">
            <span className="field__label">
              {label}
              {required && <span className="field__req"> *</span>}
            </span>
            {help && (
              <span id={helpProps.id ? undefined : helpId} className="field__help">
                {help}
              </span>
            )}
          </span>
        </label>
      ) : (
        <>
          {labelFor === false ? (
            <div id={`${id}-label`} className="field__label">
              {label}
              {required && <span className="field__req"> *</span>}
            </div>
          ) : (
            <label id={`${id}-label`} className="field__label" htmlFor={labelFor ?? id}>
              {label}
              {required && <span className="field__req"> *</span>}
            </label>
          )}
          {help && (
            <div id={helpProps.id ? undefined : helpId} className="field__help">
              {help}
            </div>
          )}
          {providedControl}
        </>
      )}
      {error && (
        <div id={errorProps.id ? undefined : errorId} className="field__error" role={errorProps.role ? undefined : "alert"}>
          {error}
        </div>
      )}
    </div>
  );
};

export const Input = ({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) => {
  const controlProps = useFieldControlProps(rest);
  return <input className={["input", className].filter(Boolean).join(" ")} {...controlProps} />;
};

export const Textarea = ({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  const controlProps = useFieldControlProps(rest);
  return <textarea className={["textarea", className].filter(Boolean).join(" ")} {...controlProps} />;
};

export const Select = ({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) => {
  const controlProps = useFieldControlProps(rest);
  return <select className={["select", className].filter(Boolean).join(" ")} {...controlProps} />;
};
